import { useMutation } from '@tanstack/react-query';

import { logError } from 'lib/sentry/logError';
import { useMutationHTTPRequest } from 'shared_DEPRECATED/hooks';

import { SprintOutcome } from 'features/planning/types';

import { showErrorToast } from 'shared/components/Toast';

type CreateSprintOutcomeInput = {
  name: string;
  sprintId: string;
  targetUserId: string;
  challengeIds?: string[];
};

type EditSprintOutcomeInput = {
  outcomeId: string;
  sprintId: string;
  name?: string;
  challengeIds?: string[];
  status?: SprintOutcome['status'];
};

type DeleteSprintOutcomeInput = {
  outcomeId: string;
  sprintId: string;
};

type SprintOutcomesResponse = {
  sprintOutcomes: SprintOutcome[];
};

type CreateSprintOutcomeResponse = {
  outcomeId: string;
};

export const useCreateSprintOutcomeMutation = ({
  options,
}: {
  options?: any;
} = {}) => {
  const { request } = useMutationHTTPRequest();

  return useMutation(
    async (input: CreateSprintOutcomeInput) => {
      const data: CreateSprintOutcomeResponse = await request({
        url: `/api/sprints/${input.sprintId}/sprint-outcomes/create`,
        body: input,
      });
      return data;
    },
    {
      onError: (error: any) => {
        options?.onError?.(error);
        showErrorToast(
          "There's an error with creating a sprint outcome. Please try again later."
        );
        logError(error);
      },
      onSuccess: (data: CreateSprintOutcomeResponse) => {
        options?.onSuccess?.(data);
      },
      ...options,
      retry: 1,
    }
  );
};

export const useEditSprintOutcomeMutation = ({
  options,
}: {
  options?: any;
} = {}) => {
  const { request } = useMutationHTTPRequest();

  return useMutation(
    async (input: EditSprintOutcomeInput) => {
      const data: SprintOutcomesResponse = await request({
        url: `/api/sprints/${input.sprintId}/sprint-outcomes/${input.outcomeId}/edit`,
        body: input,
      });
      return data;
    },
    {
      onSuccess: (data: SprintOutcomesResponse) => {
        options?.onSuccess?.(data);
      },
      onError: (error: any) => {
        options?.onError?.(error);
        showErrorToast(
          "There's an error with editing a sprint outcome. Please try again later."
        );
        logError(error);
      },
      ...options,
    }
  );
};

export const useDeleteSprintOutcomeMutation = ({
  options,
}: {
  options?: any;
} = {}) => {
  const { request } = useMutationHTTPRequest();

  return useMutation(
    async (input: DeleteSprintOutcomeInput) => {
      const data: SprintOutcomesResponse = await request({
        url: `/api/sprints/${input.sprintId}/sprint-outcomes/${input.outcomeId}/delete`,
        body: input,
      });
      return data;
    },
    {
      onSuccess: (data: SprintOutcomesResponse) => {
        options?.onSuccess?.(data);
      },
      onError: (error: any) => {
        options?.onError?.(error);
        showErrorToast(
          "There's an error with deleting a sprint outcome. Please try again later."
        );
        logError(error);
      },
      ...options,
    }
  );
};
